import { generateClient } from "aws-amplify/api";
import { fetchAuthSession } from "aws-amplify/auth";

import { GET_MY_RUNS } from "./queries";
import { ON_NEW_RUN } from "./subscriptions";
import { AppSyncRunRecord, Run, RunSettings } from "./types";

type RunsQueryResult = {
  getMyRuns?: {
    items?: AppSyncRunRecord[] | null;
    nextToken?: string | null;
  } | null;
};

type RunsSubscriptionResult = {
  onNewRun?: AppSyncRunRecord | null;
};

type RunsSubscription = {
  unsubscribe: () => void;
};

type RunsSubscriptionObservable = {
  subscribe: (handlers: {
    next: (value: { data?: RunsSubscriptionResult }) => void;
    error: (error: unknown) => void;
  }) => RunsSubscription;
};

const MAX_RUNS_PAGE_SIZE = 200;

const parseJwtPayload = (idToken: string): Record<string, unknown> => {
  const [, payload = ""] = idToken.split(".");
  return JSON.parse(atob(payload));
};

const normalizeError = (error: unknown): Error => {
  if (error instanceof Error) {
    return error;
  }
  if (typeof error === "string") {
    return new Error(error);
  }
  if (
    error &&
    typeof error === "object" &&
    "errors" in error &&
    Array.isArray(error.errors) &&
    error.errors.length > 0
  ) {
    const firstError = error.errors[0] as { message?: string };
    return new Error(firstError.message || "Unknown AppSync error");
  }
  if (
    error &&
    typeof error === "object" &&
    "message" in error &&
    typeof error.message === "string"
  ) {
    return new Error(error.message);
  }
  return new Error("Unknown AppSync error");
};

const flattenStepIds = (value: unknown): string[] => {
  if (!Array.isArray(value)) {
    return [];
  }
  const flattened: string[] = [];
  for (const item of value) {
    if (typeof item === "string") {
      flattened.push(item);
    } else if (Array.isArray(item)) {
      flattened.push(...flattenStepIds(item));
    }
  }
  return flattened;
};

const parseRunSettings = (
  runSettingsStr: string,
  organizationId: string
): RunSettings => {
  const parsed = JSON.parse(runSettingsStr) as Record<string, string>;
  return {
    inputId: parsed.input_id || parsed.inputId || "",
    organizationId:
      parsed.organization_id || parsed.organizationId || organizationId,
    workflowId: parsed.workflow_id || parsed.workflowId || "",
    settingsNameAndVersion:
      parsed.settings_name_and_version || parsed.settingsNameAndVersion || "",
    runAttemptId: parsed.run_attempt_id || parsed.runAttemptId || "",
  };
};

const runSettingsToId = (runSettings: RunSettings): string => {
  return `${runSettings.inputId}-${runSettings.organizationId}-${runSettings.workflowId}-${runSettings.settingsNameAndVersion}-${runSettings.runAttemptId}`;
};

const mapRunRecord = (run: AppSyncRunRecord): Run => {
  const runSettings = parseRunSettings(run.runSettings, run.organizationId);
  const parsedStepIds = (() => {
    try {
      return flattenStepIds(JSON.parse(run.stepIds || "[]"));
    } catch {
      return [];
    }
  })();
  const totalSteps =
    run.totalSteps ??
    (run.status === "RUN_IN_PROGRESS" ? parsedStepIds.length : null);
  const atStep =
    run.atStep ?? (run.status === "RUN_IN_PROGRESS" && totalSteps ? 0 : null);

  return {
    id: runSettingsToId(runSettings),
    displayId: null,
    runSettings,
    status: run.status,
    createdAt: run.createdAt,
    updatedAt: run.updatedAt,
    stepIds: run.stepIds || "[]",
    initiatedBy: run.initiatedBy ?? null,
    name: run.name ?? null,
    description: run.description ?? null,
    atStep,
    totalSteps,
    engineInProgress: run.engineInProgress ?? null,
  };
};

const decorateRuns = (runs: Run[]): Run[] => {
  const ascendingRuns = [...runs].sort((a, b) =>
    new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
  );
  let completedCount = 0;
  const decoratedAscendingRuns = ascendingRuns.map((run) => {
    if (
      run.status === "RUN_COMPLETED" &&
      run.runSettings.workflowId === "workflow_atomic_pivots"
    ) {
      completedCount += 1;
    }
    return {
      ...run,
      displayId: String(completedCount),
    };
  });

  return decoratedAscendingRuns.sort((a, b) =>
    new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
  );
};

const TERMINAL_STATUSES = new Set<Run["status"]>([
  "RUN_COMPLETED",
  "RUN_FAILED",
  "CANCEL_COMPLETED",
  "CANCEL_FAILED",
]);

const shouldReplaceRun = (existingRun: Run, incomingRun: Run): boolean => {
  const existingTerminal = TERMINAL_STATUSES.has(existingRun.status);
  const incomingTerminal = TERMINAL_STATUSES.has(incomingRun.status);

  if (existingTerminal && !incomingTerminal) {
    return false;
  }

  if (!existingTerminal && incomingTerminal) {
    return true;
  }

  if (
    existingRun.status === "RUN_IN_PROGRESS" &&
    incomingRun.status === "RUN_IN_PROGRESS"
  ) {
    const existingAtStep = existingRun.atStep ?? -1;
    const incomingAtStep = incomingRun.atStep ?? -1;
    if (incomingAtStep < existingAtStep) {
      return false;
    }
    if (incomingAtStep > existingAtStep) {
      return true;
    }
  }

  return (
    new Date(incomingRun.updatedAt).getTime() >=
    new Date(existingRun.updatedAt).getTime()
  );
};

const mergeRun = (runs: Run[], incomingRun: Run): Run[] => {
  const existingRunIndex = runs.findIndex((run) => run.id === incomingRun.id);
  const nextRuns = [...runs];
  if (existingRunIndex >= 0) {
    const existingRun = nextRuns[existingRunIndex];
    if (!shouldReplaceRun(existingRun, incomingRun)) {
      return decorateRuns(nextRuns);
    }
    nextRuns[existingRunIndex] = incomingRun;
  } else {
    nextRuns.push(incomingRun);
  }
  return decorateRuns(nextRuns);
};

const getAuthContext = async (
  selectedCustomerId: string | null
): Promise<{ authToken: string; organizationId: string }> => {
  const session = await fetchAuthSession({ forceRefresh: true });
  const idToken = session.tokens?.idToken?.toString();

  if (!idToken) {
    throw new Error("No authentication token available");
  }

  const payload = parseJwtPayload(idToken);
  const role = payload["custom:role"];
  const tokenOrganizationId =
    typeof payload["custom:organization"] === "string"
      ? payload["custom:organization"]
      : null;

  if (role === "ROOT") {
    const organizationId = selectedCustomerId || tokenOrganizationId;
    if (!organizationId) {
      throw new Error("No selected customer available for ROOT user");
    }
    return { authToken: idToken, organizationId };
  }

  if (!tokenOrganizationId) {
    throw new Error("No organization available in token");
  }

  return { authToken: idToken, organizationId: tokenOrganizationId };
};

export class RunsAppSyncService {
  private static instance: RunsAppSyncService;

  public static getInstance(): RunsAppSyncService {
    if (!RunsAppSyncService.instance) {
      RunsAppSyncService.instance = new RunsAppSyncService();
    }
    return RunsAppSyncService.instance;
  }

  async getMyRuns(selectedCustomerId: string | null): Promise<Run[]> {
    const { authToken, organizationId } = await getAuthContext(
      selectedCustomerId
    );
    const client = generateClient();
    const items: AppSyncRunRecord[] = [];
    let nextToken: string | null | undefined = null;

    do {
      const result = (await client.graphql({
        query: GET_MY_RUNS,
        variables: {
          organizationId,
          limit: MAX_RUNS_PAGE_SIZE,
          nextToken,
        },
        authMode: "userPool",
        authToken,
      })) as { data?: RunsQueryResult };

      const connection = result.data?.getMyRuns;
      items.push(...(connection?.items || []));
      nextToken = connection?.nextToken;
    } while (nextToken);

    return decorateRuns(items.map(mapRunRecord));
  }

  async subscribeToRuns(
    selectedCustomerId: string | null,
    handlers: {
      onRun: (run: Run) => void;
      onError: (error: Error) => void;
    }
  ): Promise<{ unsubscribe: () => void }> {
    const { authToken, organizationId } = await getAuthContext(
      selectedCustomerId
    );
    const client = generateClient();
    const observable = client.graphql({
      query: ON_NEW_RUN,
      variables: { organizationId },
      authMode: "userPool",
      authToken,
    }) as RunsSubscriptionObservable;

    const subscription = observable.subscribe({
      next: (value) => {
        const run = value.data?.onNewRun;
        if (run) {
          handlers.onRun(mapRunRecord(run));
        }
      },
      error: (error) => {
        handlers.onError(normalizeError(error));
      },
    });

    return {
      unsubscribe: () => subscription.unsubscribe(),
    };
  }
}

export const runsAppSyncService = RunsAppSyncService.getInstance();

export { decorateRuns, mergeRun };
